/**
 * PWA install + service worker registration.
 *
 * Registers /sw.js on first load (narrow-scope asset cache only). Tracks the
 * browser's beforeinstallprompt event so UI can surface an "Install app"
 * affordance; the prompt returns true/false based on whether the user
 * accepted the installation.
 */

type DeferredPromptRef = BeforeInstallPromptEvent | null;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: DeferredPromptRef = null;
const listeners = new Set<(canInstall: boolean) => void>();

function notify(canInstall: boolean): void {
  for (const listener of listeners) {
    try {
      listener(canInstall);
    } catch {
      // Listener errors are non-fatal
    }
  }
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  if (import.meta.env.DEV) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // SW registration failed — app still works, just no offline caching.
    });
  });
}

export function setupInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify(true);
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify(false);
  });
}

export function canInstallApp(): boolean {
  return deferredPrompt !== null;
}

export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) return false;
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  notify(false);
  return outcome === "accepted";
}

export function subscribeInstallAvailability(
  listener: (canInstall: boolean) => void,
): () => void {
  listeners.add(listener);
  listener(canInstallApp());
  return () => {
    listeners.delete(listener);
  };
}

export function isRunningStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  const navStandalone = (navigator as { standalone?: boolean }).standalone;
  return navStandalone === true;
}
